import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DECK } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: '', email: '', company: '', role: '', message: '' };

export default function InquiryForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Invalid email';
    if (!form.message.trim()) e.message = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => setForm({ ...form, [k]: ev.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate()) return;
    setStatus('loading');
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        role: form.role.trim() || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/inquiry`, payload);
      setStatus('success');
      toast.success('Inquiry received. We will respond within 72 hours.');
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Submission failed';
      setServerError(typeof detail === 'string' ? detail : 'Submission failed');
      setStatus('error');
      toast.error('Submission failed. Please try again.');
    }
  };

  return (
    <form data-testid={DECK.inquiryForm} onSubmit={submit} noValidate className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inq-name" className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">Full Name *</label>
          <input id="inq-name" data-testid={DECK.inquiryName} className="input-rustic" value={form.name} onChange={onChange('name')} aria-invalid={!!errors.name} placeholder="Your name" />
          {errors.name && <span className="text-xs text-red-300/90">{errors.name}</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inq-email" className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">Email *</label>
          <input id="inq-email" type="email" data-testid={DECK.inquiryEmail} className="input-rustic" value={form.email} onChange={onChange('email')} aria-invalid={!!errors.email} placeholder="you@studio.com" />
          {errors.email && <span className="text-xs text-red-300/90">{errors.email}</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inq-company" className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">Company / Studio</label>
          <input id="inq-company" data-testid={DECK.inquiryCompany} className="input-rustic" value={form.company} onChange={onChange('company')} placeholder="Production banner" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inq-role" className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">Role</label>
          <input id="inq-role" data-testid={DECK.inquiryRole} className="input-rustic" value={form.role} onChange={onChange('role')} placeholder="Producer, Distributor, OTT Head…" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="inq-msg" className="font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">Message *</label>
        <textarea id="inq-msg" data-testid={DECK.inquiryMessage} className="textarea-rustic" value={form.message} onChange={onChange('message')} aria-invalid={!!errors.message} placeholder="Tell us briefly about your interest — first-look, distribution, OTT, festival." />
        {errors.message && <span className="text-xs text-red-300/90">{errors.message}</span>}
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between mt-2">
        <p className="text-xs text-[color:var(--text-dim)]">We will respond within 72 hours with the full deck and budget.</p>
        <button type="submit" data-testid={DECK.inquirySubmit} disabled={status === 'loading'} className="btn-gold disabled:opacity-70">
          {status === 'loading' ? 'Sending…' : 'Submit Inquiry'}
        </button>
      </div>
      {status === 'success' && (
        <div data-testid={DECK.inquirySuccess} className="mt-2 text-sm text-[color:var(--gold)] font-ui tracking-[0.18em] uppercase">
          Inquiry received. Expect a response within 72 hours.
        </div>
      )}
      {status === 'error' && (
        <div data-testid={DECK.inquiryError} className="mt-2 text-sm text-red-300/90 font-ui tracking-[0.18em] uppercase">
          {serverError || 'Submission failed. Please try again.'}
        </div>
      )}
    </form>
  );
}
